import { useState, useCallback } from 'react';

import {
  Autocomplete, RadioGroup, RadioButton,
  DatePicker, TimePickerEvent,
  TextField, AutocompleteSearchData,
  SelectData, SelectEvent, Button,
  Tooltip, IconWrapper
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { ValidationObject } from "@king-ict/eupisi-ui/hooks";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { hrDate, nameof } from '@king-ict/eupisi-ui/utils';
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  uploadDocument,
  searchMunicipalities, getMunicipalityById,
  getMunicipalityByTownId, searchTowns, getTownById,
  DocumentUploadRequest
} from "../../api/nisooApi";
import UploadButton from "../../components/UploadButton";
import { DocumentType } from "../../models/Document";
import { Institution } from '../../models/Institution';
import { InstitutionLocation, LocationDeleteState, LocationState } from "../../models/Institution/InstitutionLocation";
import { MunicipalityCodeBook } from "../../models/Municipality";
import { TownCodeBook } from "../../models/Town";
import { RoleEnum } from '../../models/User/RoleEnum';
import { institutionDetailsRoute, institutionEditRoute } from "../../routes/routes";
import styles from "./Institution.module.scss";

type LocationProps = {
  name: string;
  location: InstitutionLocation,
  locations: InstitutionLocation[],
  institutionTypeId: number,
  roleId: RoleEnum | undefined,
  panelIndex: number,
  readOnly: boolean,
  getErrorForPath: (path: string) => string | undefined;
  onChange: (e: any) => void;
}

export default function LocationPanel({ name, location, institutionTypeId, roleId, panelIndex, readOnly, getErrorForPath, onChange, locations }: LocationProps): JSX.Element {
  const [county, setCounty] = useState<string | undefined>(undefined);
  const [municipalityId, setMunicipalityId] = useState<number | undefined>(undefined);
  const history = useHistory();
  const { t } = useTranslation();
  const adultEducationInstitutinType = 40;
  var institutionRoute = readOnly ? institutionDetailsRoute : institutionEditRoute;
  var canEdit = !readOnly && location.deleteState !== LocationDeleteState.CannotDelete && (location.activityState !== LocationState.Deactivated || (roleId === RoleEnum.MZOM));
  var deactivatedStyle = readOnly ? `${styles.locationPanel} ${styles.locationPanelDeactivated}` :
    `${styles.locationPanel} ${styles.locationPanelDeactivated} ${styles.locationPanelWider}`;
  var panelId = "locationPanel" + panelIndex;

  const handleRemoveLocation = (event: React.MouseEvent<any, MouseEvent>) => {
    var newLocations = locations.slice();
    newLocations[panelIndex].deleteState = LocationDeleteState.Deleted;

    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newLocations
      }
    });
  };

  const handleActivityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    var newLocations = locations.slice();
    newLocations[panelIndex].activityState = location.activityState === LocationState.Active ? LocationState.Deactivated : LocationState.Active;

    onChange({
      ...e,
      target: {
        ...e.target,
        name: name,
        value: newLocations
      }
    });
  };

  const handleLocationTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    var newLocations = locations.slice();
    const checkedLocation = parseInt(e.target.value);

    newLocations[panelIndex].locationTypeId = checkedLocation === 1 ? 1 : checkedLocation === 2 ? 2 : 3;

    onChange({
      ...e,
      target: {
        ...e.target,
        name: name,
        value: newLocations
      }
    });
  }

  const handleDateChange = (e: React.ChangeEvent<TimePickerEvent>) => {
    var newLocations = locations.slice();
    newLocations[panelIndex].deactivateDate = e.target.value;

    onChange({
      ...e,
      target: {
        ...e.target,
        name: name,
        value: newLocations
      }
    });
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    var newLocations = locations.slice();
    newLocations[panelIndex].address = e.target.value;

    onChange({
      ...e,
      target: {
        ...e.target,
        name: name,
        value: newLocations
      }
    });
  };

  const handleMunicipalityChange = (e: React.ChangeEvent<SelectEvent>) => {
    var newLocations = locations.slice();
    var municipalityId = e?.target?.value as number;

    newLocations[panelIndex].town = {
      id: 0, name: "", countyId: 0,
      countyName: "",
      municipalityId: municipalityId, municipalityName: ""
    };

    setMunicipalityId(municipalityId);

    onChange({
      ...e,
      target: {
        ...e.target,
        name: name,
        value: newLocations
      }
    });
  }

  const handleMunicipalitySearch = useCallback(async (search: AutocompleteSearchData) => {
    if (search.text && search.text.length >= 3) {
      const municipalitiesFiltered = await searchMunicipalities(search.text, undefined);
      return {
        valueProp: "id",
        textProp: "name",
        data: municipalitiesFiltered
      } as SelectData<MunicipalityCodeBook>;
    }
    else if (search.value && typeof (search.value) === 'number') {
      const municipality = await getMunicipalityById(search.value);
      setCounty(municipality.countyName);
      return {
        valueProp: "id",
        textProp: "name",
        data: [municipality]
      } as SelectData<MunicipalityCodeBook>;
    }

    return {
      valueProp: "id",
      textProp: "name"
    } as SelectData<MunicipalityCodeBook>;
  }, []
  )

  const handleTownChange = async (e: React.ChangeEvent<SelectEvent>) => {
    var newLocations = locations.slice();
    var townId = e?.target?.value as number;

    if (townId) {
      await getMunicipalityByTownId(townId).then((municipality) => {
        newLocations[panelIndex].town = {
          id: townId, name: "", countyId: municipality.countyId,
          countyName: municipality.countyName,
          municipalityId: municipality.id, municipalityName: municipality.name
        };
        setMunicipalityId(municipality.id);
        setCounty(municipality.countyName);
      });
    }
    else {
      newLocations[panelIndex].town = {
        id: 0, name: "", countyId: 0,
        countyName: "",
        municipalityId: 0, municipalityName: ""
      };

      setMunicipalityId(0);
      setCounty("");
    }

    onChange({
      ...e,
      target: {
        ...e.target,
        name: name,
        value: newLocations
      }
    });
  }

  const handleTownSearch = useCallback(async (search: AutocompleteSearchData) => {

    if (search.text && search.text.length >= 3) {
      const townsFiltered = await searchTowns(search.text, municipalityId);
      return {
        valueProp: "id",
        textProp: "name",
        data: townsFiltered
      } as SelectData<TownCodeBook>;
    }
    else if (search.value && typeof (search.value) === 'number') {
      const town = await getTownById(search.value);
      setMunicipalityId(town.municipalityId);
      setCounty(town.countyName);
      return {
        valueProp: "id",
        textProp: "name",
        data: [town]
      } as SelectData<TownCodeBook>;
    }

    return {
      valueProp: "id",
      textProp: "name"
    } as SelectData<MunicipalityCodeBook>;
  }, [municipalityId]
  )

  const handleLocationUsageAgreementAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    var newLocations = locations.slice();

    const newLocationUsageAgreement: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };

    locations[panelIndex].locationUsageAgreement = newLocationUsageAgreement;

    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newLocations
      }
    });
  };

  const handleLocationUsageAgreementRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!location.locationUsageAgreement || location.locationUsageAgreement.id === undefined) {
      return;
    }

    var newLocations = locations.slice();
    newLocations[panelIndex].locationUsageAgreement = undefined;

    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newLocations
      }
    });
  };

  const handleLocationMinimumRequirementSolutionAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    const newLocationMinimumRequirementSolution: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };

    var newLocations = locations.slice();
    newLocations[panelIndex].locationMinimumRequirementSolution = newLocationMinimumRequirementSolution;

    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newLocations
      }
    });
  }

  const handleLocationMinimumRequirementSolutionRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!location.locationMinimumRequirementSolution || location.locationMinimumRequirementSolution.id === undefined) {
      return;
    }

    var newLocations = locations.slice();
    newLocations[panelIndex].locationMinimumRequirementSolution = undefined;

    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newLocations
      }
    });

  };

  var locationTypeRow, locationTypeCaptionRow, addressCol, municipalityCol, townCol, deactivateDate, deactivateRadioButtons;
  var removeLocationWidth = location.activityState === LocationState.Deactivated ? 12 : 8, removeLocation;

  const getDeactivateCaption = () => {
    if (location.activityState === LocationState.Deactivated) {
      if (location.deactivateDate) {
        return t("institution.locationDeactivatedFrom") + hrDate(location.deactivateDate);
      }
      return t("institution.locationDeactivatedFrom");
    }
    return t("common.yes");
  }

  if (readOnly || location.activityState === LocationState.Deactivated) {
    locationTypeRow =
      <Row>
        <Col md={4}>
          <TextField
            plaintext
            label={t("institution.locationType")}
            readonly
            value={location.locationType!}
            className={styles.textFieldLabelLocation}
          />
        </Col>
        <Col md={7}>
          <TextField
            plaintext
            label={t("institution.locationIsActive")}
            readonly
            value={getDeactivateCaption()}
            className={styles.textFieldLabelLocation}
          />
        </Col>
        <Col md={1}>
          <div className={styles.infoTooltipPanel}>
            <Tooltip
              overlay={<div className={styles.infoTooltip}>{t("institution.locationEditModeNote")}</div>}
              placement="left"
            >
              <IconWrapper color="primary">
                <InfoIcon />
              </IconWrapper>
            </Tooltip>
          </div>
        </Col>
      </Row>
  }
  else if (!canEdit) {
    locationTypeRow =
      <Row>
        <Col md={11}>
          <TextField
            plaintext
            label={t("institution.locationType")}
            readonly
            value={location.locationType!}
            className={styles.textFieldLabelLocation}
          />
        </Col>
        <Col md={1}>
          <div className={styles.infoTooltipPanel}>
            <Tooltip
              overlay={<div className={styles.infoTooltip}>{t("institution.locationEditModeNote")}</div>}
              placement="left"
            >
              <IconWrapper color="primary">
                <InfoIcon />
              </IconWrapper>
            </Tooltip>
          </div>
        </Col>
      </Row>
  }

  if (!canEdit) {
    addressCol =
      <Col md={4}>
        <TextField
          plaintext
          label={t("institution.locationAddress")}
          readonly
          value={location.address}
          className={styles.textFieldLabelLocation}
        />
      </Col>
    townCol =
      <Col md={4}>
        <TextField
          plaintext
          label={t("institution.locationTown")}
          readonly
          value={location.town.name}
          className={styles.textFieldLabelLocation}
        />
      </Col>

    municipalityCol =
      <Col md={4}>
        <TextField
          plaintext
          label={t("institution.locationMunicipality")}
          readonly
          value={location.town.municipalityName}
          className={styles.textFieldLabelLocation}
        />
      </Col>
  }
  else {

    locationTypeCaptionRow =
      <Row>
        <Col md={11}>
          <span className={styles.locationActiveCaption}>{t("institution.locationType")}</span>
        </Col>
        <Col md={1}>
          <div className={styles.infoTooltipPanel}>
            <Tooltip
              overlay={<div className={styles.infoTooltip}>{t("institution.locationEditModeNote")}</div>}
              placement="left"
            >
              <IconWrapper color="primary">
                <InfoIcon />
              </IconWrapper>
            </Tooltip>
          </div>
        </Col>
      </Row>

    locationTypeRow =
      <Row>
        <Col md={12} className={styles.locationTypeRadioButtonRow}>
          <RadioGroup name="newLocationType" title={""} onChange={handleLocationTypeChange}>
            <Row>
              <Col md={4}>
                <RadioButton
                  key="locationTypeOutsideOffice"
                  ariaLabel={t("institution.locationOutsideOffice")}
                  label={t("institution.locationOutsideOffice")}
                  value='1'
                  checked={location.locationTypeId === 1}
                />
              </Col>
              {adultEducationInstitutinType === institutionTypeId && false &&
                <Col md={4}>
                  <RadioButton
                    key="locationAdultEducationDepartment"
                    ariaLabel={t("institution.adultEducationDepartment")}
                    label={t("institution.adultEducationDepartment")}
                    value='3'
                    checked={location.locationTypeId === 3}
                  />
                </Col>
              }
              {adultEducationInstitutinType === institutionTypeId &&
                <Col md={4}>
                  <RadioButton
                    key="locationTypeBranchOffice"
                    ariaLabel={t("institution.branchOffice")}
                    label={t("institution.branchOffice")}
                    value='2'
                    checked={location.locationTypeId === 2}
                  />
                </Col>
              }
            </Row>
          </RadioGroup>
        </Col>
      </Row>

    addressCol =
      <Col md={4}>
        <TextField
          label={t("institution.locationAddress")}
          value={location.address}
          name={nameof<typeof location>("address")}
          onChange={handleAddressChange}
          errorText={getErrorForPath(`locations${panelIndex}.address`)}
          className={styles.textFieldLabelLocation}
        />
      </Col>

    townCol =
      <Col md={4}>
        <Autocomplete
          label={t("institution.locationTown")}
          value={location.town.id}
          name={nameof<typeof location>("town")}
          onChange={handleTownChange}
          getOptions={handleTownSearch}
          errorText={getErrorForPath(`locations${panelIndex}.town.id`)}
        />
      </Col>

    municipalityCol =
      <Col md={4}>
        <Autocomplete
          label={t("institution.locationMunicipality")}
          name={"municipalityId"}
          value={municipalityId}
          onChange={handleMunicipalityChange}
          getOptions={handleMunicipalitySearch}
        />
      </Col>
  }

  if (location.activityState === LocationState.Deactivated) {
    removeLocationWidth = 4;
    deactivateDate =
      <Col md={4}>
        <DatePicker
          label={t("institution.laocationDeactivateDate")}
          value={location.deactivateDate}
          name={nameof<typeof location>("deactivateDate")}
          onChange={handleDateChange}
          className={styles.datePicker}
          minYear={new Date().getFullYear() - 25}
          maxYear={new Date().getFullYear() + 25}
          plaintext={!canEdit}
        />
      </Col>
  }

  if (canEdit) {
    removeLocation =
      <Col md={removeLocationWidth} className={styles.removeLocationCol}>
        <Button
          boxless
          onClick={handleRemoveLocation}
        >
          {t("institution.removeLocation")}
        </Button>
      </Col>;
  }

  if (canEdit) {
    deactivateRadioButtons =
      <Col md={4}>
        <div>
          <span className={styles.locationActiveCaption}>{t("institution.locationIsActive")}</span>
          <Tooltip
            overlay={<div className={styles.infoTooltip}>{t("institution.locationDeactivationNote")}</div>}
            placement="right"
          >
            <IconWrapper color="primary">
              <InfoIcon />
            </IconWrapper>
          </Tooltip>
        </div>
        <RadioGroup name={"deactivateLocation-" + location.id.toString()} title={""} onChange={handleActivityChange}>
          <Row>
            <Col md={4}>
              <RadioButton
                key={'deactivateYes-' + location.id.toString()}
                ariaLabel={t("common.yes")}
                label={t("common.yes")}
                value='1'
                checked={location.activityState === LocationState.Active}
              />
            </Col>
            <Col md={8}>
              <RadioButton
                key={'deactivateNo-' + location.id.toString()}
                ariaLabel={t("common.no")}
                label={t("common.no")}
                value='2'
                checked={location.activityState === LocationState.Deactivated}
              />
            </Col>
          </Row>
        </RadioGroup>
      </Col>
  }

  return (
    <>
      <div className={location.activityState === LocationState.Deactivated ? deactivatedStyle : styles.locationPanel} id={panelId}>
        {locationTypeCaptionRow}
        {locationTypeRow}

        <Row className={styles.autoCompliteRow}>
          {addressCol}
          {townCol}
          {municipalityCol}
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              plaintext
              label={t("institution.locationCounty")}
              readonly
              value={location.id === 0 ? county : location.town.countyName}
              className={styles.textFieldLabelWider}
            />
          </Col>
          <Col md={4} className={canEdit ? styles.uplaodButtonLabel : styles.hideUploadButton}>
            <UploadButton
              label={t("institution.locationUsageAgreement")}
              name={nameof<typeof location>("locationUsageAgreement")}
              onFileAdded={handleLocationUsageAgreementAdded}
              onFileRemoved={handleLocationUsageAgreementRemoved}
              value={location.locationUsageAgreement}
              disabled={!canEdit}
              toolTip={t("document.imagesAndPdfTooltip")}
              fileFilter={t("document.imagesAndPdfFilter")}
              errorText={readOnly ? '' : getErrorForPath(`locations${panelIndex}.locationUsageAgreement`)}
              documentType={DocumentType.LocationUsageAgreement}
            />
          </Col>
          <Col md={4} className={canEdit ? styles.uplaodButtonLabel : styles.hideUploadButton}>
            <UploadButton
              label={t("institution.locationMinimumRequirements")}
              name={nameof<typeof location>("locationMinimumRequirementSolution")}
              onFileAdded={handleLocationMinimumRequirementSolutionAdded}
              onFileRemoved={handleLocationMinimumRequirementSolutionRemoved}
              value={location.locationMinimumRequirementSolution}
              disabled={!canEdit}
              toolTip={t("document.imagesAndPdfTooltip")}
              fileFilter={t("document.imagesAndPdfFilter")}
              errorText={readOnly ? '' : getErrorForPath(`locations${panelIndex}.locationMinimumRequirementSolution`)}
              documentType={DocumentType.LocationMinimumRequirementSolution}
            />
          </Col>
        </Row>
        <Row>
          {deactivateRadioButtons}
          {deactivateDate}
          {removeLocation}
        </Row>
      </div>
    </>
  );
}